/**
 * useAlertsStore - Hook for alert management with immediate API sync.
 *
 * All operations are immediately pushed to the server.
 * No local state - the server is the single source of truth.
 */

import { createResourceHook } from "../use-resource-store";
import {
  useAlerts,
  useCreateAlert,
  useUpdateAlert,
  useDeleteAlert,
} from "@/api/alert";
import { Alert, AlertCreate } from "@/models/alert";

/**
 * Hook for alert management with immediate API sync.
 *
 * @example
 * ```tsx
 * function AlertList() {
 *   const {
 *     resources: alerts,
 *     create,
 *     update,
 *     remove,
 *     isLoading,
 *     isUpdating,
 *   } = useAlertsStore();
 *
 *   const handleUpdate = async (id: string, name: string) => {
 *     await update(id, { name });
 *   };
 *
 *   return (
 *     <div>
 *       {alerts.map(a => (
 *         <AlertItem key={a.id} alert={a} onUpdate={handleUpdate} />
 *       ))}
 *     </div>
 *   );
 * }
 * ```
 */
export const useAlertsStore = createResourceHook<Alert, AlertCreate>({
  resourceName: "alert",
  resourcePath: "alerts",

  useQueryHook: () => {
    const { data, isLoading, error, refetch } = useAlerts();
    return {
      data: data ?? undefined,
      isLoading,
      error: error as Error | null,
      refetch,
    };
  },

  useCreateMutation: () => {
    const mutation = useCreateAlert();
    return {
      mutateAsync: async (data: AlertCreate) => {
        const result = await mutation.mutateAsync(data);
        return result as Alert;
      },
    };
  },

  useUpdateMutation: () => {
    const mutation = useUpdateAlert();
    return {
      mutateAsync: async (
        data: { uriParams: { id: string } } & Record<string, unknown>
      ) => {
        const { uriParams, ...rest } = data;
        const result = await mutation.mutateAsync({
          uriParams,
          ...rest,
        } as Parameters<typeof mutation.mutateAsync>[0]);
        return result as Alert;
      },
    };
  },

  useDeleteMutation: () => {
    const mutation = useDeleteAlert();
    return {
      mutateAsync: async (data: { uriParams: { id: string } }) => {
        await mutation.mutateAsync(
          data as Parameters<typeof mutation.mutateAsync>[0]
        );
      },
    };
  },
});

export default useAlertsStore;
